import { Table } from '@components/table'
import type { ColumnsType, PageRead } from '@components/table/types'
import { useReparseNotification } from '@services/banking/mutations/useReparseNotification'
import { useBankNotificationsTable } from '@services/banking/queries/useBankNotificationsTable'
import { Ban, Link2, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'

import type { BankNotification, NotificationStatus } from '../types'
import { IgnoreNotificationDialog } from './IgnoreNotificationDialog'
import { MatchNotificationDialog } from './MatchNotificationDialog'

/** Predvolený filter je „nespárované" — to je fronta, ktorú admin rieši.
 * Prázdna hodnota znamená všetky stavy. */
const STATUS_FILTERS: Array<{ value: string; key: string }> = [
  { value: 'UNMATCHED', key: 'unmatched' },
  { value: 'UNPARSED', key: 'unparsed' },
  { value: 'MATCHED', key: 'matched' },
  { value: 'IGNORED', key: 'ignored' },
  { value: '', key: 'all' }
]

export function BankNotificationsTable() {
  const { t } = useTranslation('banking')
  const [searchParams, setSearchParams] = useSearchParams()
  const [matching, setMatching] = useState<BankNotification | null>(null)
  const [ignoring, setIgnoring] = useState<BankNotification | null>(null)

  const query = useBankNotificationsTable()
  const reparse = useReparseNotification()

  const activeStatus = searchParams.get('status') ?? 'UNMATCHED'

  const setStatus = (value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value) {
      next.set('status', value)
    } else {
      // Prázdny reťazec by backend dostal ako filter na prázdny stav.
      next.set('status', '')
    }
    // Zmena filtra musí vrátiť stránkovanie na začiatok, inak zostane
    // používateľ na strane, ktorá už neexistuje.
    next.delete('offset')
    setSearchParams(next)
  }

  const columns: ColumnsType<BankNotification> = [
    {
      title: t('columns.occurredAt'),
      dataIndex: 'occurred_at',
      render: (value: string | null) =>
        value ? new Date(value).toLocaleString('sk-SK') : '—'
    },
    {
      title: t('columns.amount'),
      dataIndex: 'amount',
      align: 'right',
      render: (value: string | null, record: BankNotification) =>
        value ? (
          <span
            className={
              record.direction === 'DEBIT' ? 'text-ink' : 'text-emerald-700'
            }
          >
            {record.direction === 'DEBIT' ? '−' : '+'}
            {value} {record.currency}
          </span>
        ) : (
          '—'
        )
    },
    {
      title: t('columns.counterparty'),
      dataIndex: 'counterparty_name',
      render: (value: string, record: BankNotification) => (
        <div>
          <div className="font-medium text-ink">{value || '—'}</div>
          <div className="text-xs text-ink/50">{record.counterparty_iban}</div>
        </div>
      )
    },
    {
      title: t('columns.symbols'),
      dataIndex: 'variable_symbol',
      render: (value: string, record: BankNotification) => (
        <code className="rounded bg-ink/[0.06] px-2 py-0.5 text-xs">
          {value || '—'} / {record.specific_symbol || '—'}
        </code>
      )
    },
    {
      title: t('columns.status'),
      dataIndex: 'status',
      render: (value: NotificationStatus, record: BankNotification) => (
        <div>
          <div>{t(`statuses.${value}`)}</div>
          {record.parse_error && (
            <div
              className="max-w-56 truncate text-xs text-ink/50"
              title={record.parse_error}
            >
              {record.parse_error}
            </div>
          )}
          {record.ignore_reason && (
            <div className="text-xs text-ink/50">{record.ignore_reason}</div>
          )}
        </div>
      )
    },
    {
      title: t('columns.rent'),
      dataIndex: 'rent',
      render: (_: unknown, record: BankNotification) =>
        record.rent ? (
          <div className="text-xs">
            <div className="text-ink">{record.rent.label || '—'}</div>
            <div className="text-ink/50">VS {record.rent.variable_symbol}</div>
          </div>
        ) : (
          <span className="text-ink/40">—</span>
        )
    },
    {
      title: '',
      dataIndex: 'id',
      align: 'right',
      render: (_: string, record: BankNotification) => (
        <div className="flex justify-end gap-1">
          {record.status === 'UNMATCHED' && (
            <>
              <Button
                size="sm"
                variant="ghost"
                title={t('actions.match')}
                onClick={() => setMatching(record)}
              >
                <Link2 className="size-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                title={t('actions.ignore')}
                onClick={() => setIgnoring(record)}
              >
                <Ban className="size-4" />
              </Button>
            </>
          )}
          {record.status === 'UNPARSED' && (
            <Button
              size="sm"
              variant="ghost"
              title={t('actions.reparse')}
              disabled={reparse.isPending}
              onClick={() => reparse.mutate(record.id)}
            >
              <RefreshCw className="size-4" />
            </Button>
          )}
        </div>
      )
    }
  ]

  return (
    <div>
      <div className="mb-3 flex flex-wrap gap-2">
        {STATUS_FILTERS.map((filter) => (
          <Button
            key={filter.key}
            size="sm"
            variant={activeStatus === filter.value ? 'default' : 'outline'}
            onClick={() => setStatus(filter.value)}
          >
            {t(`filters.${filter.key}`)}
          </Button>
        ))}
      </div>

      {/* Notifikácia nemá detailnú stránku — riadok ukazuje všetko podstatné
          a akcie sú tlačidlá. Bez tohto by klik na riadok navigoval na
          neexistujúcu /admin/bank-notifications/<id> a router by spadol na
          index redirect, teda na dashboard. */}
      <Table<PageRead<BankNotification>>
        query={query}
        columns={columns}
        id="bank-notifications"
        hasClick={false}
      />

      <MatchNotificationDialog
        notification={matching}
        onClose={() => setMatching(null)}
      />
      <IgnoreNotificationDialog
        notification={ignoring}
        onClose={() => setIgnoring(null)}
      />
    </div>
  )
}
